import { useState, useCallback } from 'react';

/**
 * Hook for programmatically controlling a Modal
 *
 * @returns Modal state and control functions
 *
 * @example
 * ```tsx
 * const { isOpen, openModal, closeModal, toggleModal } = useModal();
 *
 * // Open modal programmatically
 * useEffect(() => {
 *   if (someCondition) {
 *     openModal();
 *   }
 * }, [someCondition, openModal]);
 *
 * return (
 *   <Modal
 *     dataTestId="my-modal"
 *     isOpen={isOpen}
 *     onClose={closeModal}
 *     title="My Modal"
 *   >
 *     Content
 *   </Modal>
 * );
 * ```
 */
export const useModal = (initialState = false) => {
  const [isOpen, setIsOpen] = useState(initialState);

  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleModal = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return {
    /** Whether the modal is currently open */
    isOpen,
    /** Function to open the modal */
    openModal,
    /** Function to close the modal */
    closeModal,
    /** Function to toggle the modal open/closed state */
    toggleModal,
  };
};

export default useModal;
